import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dropinApi } from "../api";
import type {
  FacilityWaiverDocumentResponse,
  FacilityWaiverStatusResponse,
  WaiverAcceptanceResponse,
} from "../types/generated";

export interface WaiverGate {
  /** True while the per-person gate must block check-in/booking. The gate FAILS
   * CLOSED: it blocks whenever the waiver requirement is not affirmatively known
   * to be satisfied — i.e. while the status read is loading, when it errors, OR
   * when the facility has a current waiver this user has not accepted. It is only
   * false once status has RESOLVED to "no published waiver" (current_version_id
   * == null → nothing to accept) or "already accepted". */
  isBlocked: boolean;
  /** The current waiver version the user must accept, or null when none. */
  currentVersionId: string | null;
  /** The published waiver text (for the "Read the waiver" sheet), or null. */
  currentWaiver: FacilityWaiverDocumentResponse | null | undefined;
  /** Still resolving the required status/text snapshot. */
  isLoading: boolean;
  /** True when the required waiver snapshot could not be confirmed. The gate
   * stays closed and the caller shows an honest retry state. */
  statusError: boolean;
  /** Re-read status and the current legal text as one waiver snapshot. */
  retryStatus: () => void;
  /** Accept the CURRENT waiver for this user only, then re-read status. An
   * optional callback runs when this specific POST settles (success or error),
   * so a caller can hold a synchronous dismissal fence across its retry. */
  accept: (onSettled?: () => void) => void;
  /** True while the accept mutation is in flight. */
  isAccepting: boolean;
  /** True if accepting failed (the caller surfaces an inline error). */
  acceptError: boolean;
}

// Query keys are inlined as literal tuples (NOT imported from any app's
// query-key factory) so this shared hook has no reverse dependency on a
// consuming app. They mirror the dropin namespace so a single facility's
// waiver state stays in one cache slot regardless of which path reads it.
function waiverStatusKey(facilityId: string) {
  return ["dropin", "waiver-status", facilityId] as const;
}
function waiverKey(facilityId: string) {
  return ["dropin", "waiver", facilityId] as const;
}

/**
 * Per-person, NON-DELEGABLE facility-waiver gate, composed in the frontend
 * because neither DropInService.check_in() nor the court-booking path enforces
 * the waiver server-side today (tracked backend parity gap — #1316). It reads
 * waiver/status + the current waiver text, and accepts the current version for
 * THIS user only.
 *
 * `accept()` always posts the CURRENT `current_version_id` from status — never
 * a stale or caller-supplied id — and on success invalidates status so the
 * gate re-reads and unblocks. There is deliberately no "accept for others" path.
 *
 * Promoted out of modules/dropin into @bw/shared so both the drop-in and the
 * court-booking paths consume one authoritative gate (module isolation — no
 * sibling import; clean migration — no duplicate / dual path).
 */
export function useWaiverGate(facilityId: string): WaiverGate {
  const queryClient = useQueryClient();

  const statusQuery = useQuery<FacilityWaiverStatusResponse>({
    queryKey: waiverStatusKey(facilityId),
    queryFn: () => dropinApi.getWaiverStatus(facilityId),
    enabled: Boolean(facilityId),
    refetchOnMount: "always",
  });

  const waiverQuery = useQuery<FacilityWaiverDocumentResponse | null>({
    queryKey: waiverKey(facilityId),
    queryFn: () => dropinApi.getCurrentWaiver(facilityId),
    enabled: Boolean(facilityId),
    refetchOnMount: "always",
  });

  const currentVersionId = statusQuery.data?.current_version_id ?? null;
  const hasAccepted = statusQuery.data?.has_accepted_current ?? false;
  const requiresAcceptance = currentVersionId !== null && !hasAccepted;
  const currentWaiver =
    currentVersionId === null
      ? null
      : waiverQuery.data?.version_id === currentVersionId
        ? waiverQuery.data
        : undefined;
  const waiverTextMissing =
    requiresAcceptance &&
    waiverQuery.isSuccess &&
    !waiverQuery.isFetching &&
    currentWaiver === undefined;
  const waiverTextError =
    requiresAcceptance && (waiverQuery.isError || waiverTextMissing);
  const waiverTextLoading =
    requiresAcceptance && currentWaiver === undefined && !waiverTextError;
  // FAIL CLOSED: a liability waiver gate must block whenever acceptance is not
  // affirmatively known to be satisfied. While the status read is loading or
  // has errored, the requirement is UNKNOWN — treat unknown as blocked. The
  // gate only opens once status RESOLVES to "no published waiver" (null version)
  // or "already accepted". Check-in and booking repeat this gate server-side;
  // the client gate provides early feedback and never replaces that authority.
  const isBlocked =
    statusQuery.isFetching || statusQuery.isError || requiresAcceptance;

  const acceptMutation = useMutation<WaiverAcceptanceResponse, unknown, void>({
    mutationFn: () => {
      if (
        currentVersionId === null ||
        currentWaiver?.version_id !== currentVersionId
      ) {
        return Promise.reject(
          new Error("The current waiver text is not ready to accept"),
        );
      }
      return dropinApi.acceptWaiver(facilityId, currentVersionId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: waiverStatusKey(facilityId),
      });
    },
  });

  return {
    isBlocked,
    currentVersionId,
    currentWaiver,
    isLoading: statusQuery.isFetching || waiverTextLoading,
    statusError: statusQuery.isError || waiverTextError,
    retryStatus: () => {
      void statusQuery.refetch();
      void waiverQuery.refetch();
    },
    accept: (onSettled) =>
      acceptMutation.mutate(
        undefined,
        onSettled ? { onSettled: () => onSettled() } : undefined,
      ),
    isAccepting: acceptMutation.isPending,
    acceptError: acceptMutation.isError,
  };
}
